import React, { CSSProperties } from 'react';

import { MapAdvancedMarker } from '@app/components/molecules/map-advanced-marker';
import { MarkerPriorityEnum } from '@app/enums';
import { ClusterType, MarkerTypeEnum } from '@app/models/map-marker.model';

import { areMapMarkerRendererPropsEqual } from './map-marker-renderer.memo';
import { MapMarkerRendererProps } from './map-marker-renderer.props';

const getMarkerZIndex = (
    id: string,
    type: MarkerTypeEnum,
    hoveredId: string | null,
    selectedId: string | null,
    orderIndex: number,
    totalItems: number,
): number => {
    const baseZIndex = totalItems + 10;

    let priority: MarkerPriorityEnum = MarkerPriorityEnum.Normal;

    if (id === selectedId) {
        priority = MarkerPriorityEnum.Selected;
    } else if (id === hoveredId) {
        priority = MarkerPriorityEnum.Hovered;
    } else if (type === MarkerTypeEnum.Cluster) {
        priority = MarkerPriorityEnum.Cluster;
    }

    return priority * baseZIndex + orderIndex;
};

const getMarkerStyle = (
    markerId: string,
    hoveredId: string | null,
    selectedId: string | null,
): CSSProperties => {
    const scale = [hoveredId, selectedId].includes(markerId) ? 1.3 : 1;
    return {
        transform: `scale(${scale})`,
        transition: 'transform 0.1s ease-in-out',
    };
};

const MapMarkerRendererComponent = ({
    marker,
    index,
    totalItems,
    hoveredId,
    selectedId,
    onAccommodationMarkerClick,
    onClusterMarkerClick,
    onMouseEnter,
    onMouseLeave,
}: MapMarkerRendererProps): JSX.Element => {
    const isCluster = marker.isCluster;
    const markerId = marker.id;
    const markerStyle = getMarkerStyle(markerId, hoveredId, selectedId);
    const markerZIndex = getMarkerZIndex(
        markerId,
        isCluster ? MarkerTypeEnum.Cluster : MarkerTypeEnum.Accommodation,
        hoveredId,
        selectedId,
        index,
        totalItems,
    );

    const commonProps = {
        position: marker.position,
        zIndex: markerZIndex,
        style: markerStyle,
        onMouseEnter: () => onMouseEnter(markerId),
        onMouseLeave: onMouseLeave,
    };

    if (isCluster) {
        return (
            <MapAdvancedMarker
                type={MarkerTypeEnum.Cluster}
                count={marker.count}
                onMarkerClick={() => onClusterMarkerClick(marker as ClusterType)}
                {...commonProps}
            />
        );
    }

    const { rate, baseRate, currency, reservationType, cheapestRateCheckIn, cheapestRateCheckOut, isUnavailable, isInitiallySelected, id } =
        marker;

    return (
        <MapAdvancedMarker
            type={MarkerTypeEnum.Accommodation}
            id={id}
            rate={rate}
            currency={currency}
            isUnavailable={isUnavailable}
            isInitiallySelected={isInitiallySelected}
            reservationType={reservationType}
            isSelected={selectedId === markerId}
            onMarkerClick={markerEl =>
                onAccommodationMarkerClick(
                    markerId,
                    markerEl,
                    rate,
                    baseRate,
                    currency,
                    reservationType,
                    cheapestRateCheckIn,
                    cheapestRateCheckOut,
                    isUnavailable,
                )
            }
            {...commonProps}
        />
    );
};

export const MapMarkerRenderer = React.memo(
    MapMarkerRendererComponent,
    areMapMarkerRendererPropsEqual,
);
